import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { components } from '../../api/generated/schema'
import type { Envelope, Notice, NoticeLevel } from '../../lib/envelope'
import { patchSheets } from '../../lib/sheetCache'
import { encounterKeys } from './queries'
import type { EncounterSections, EncounterSheet } from './types'

type Schema = components['schemas']

export type EncounterEnvelope = Envelope<EncounterSections>

const NOTICE_LEVELS: NoticeLevel[] = ['info', 'warn', 'error']

function toNotice(raw: Schema['Notice']): Notice {
  const level = raw.level as NoticeLevel | undefined
  return {
    level: level !== undefined && NOTICE_LEVELS.includes(level) ? level : 'info',
    code: raw.code ?? 'unknown',
    message: raw.message ?? '',
  }
}

export function toEnvelope(dto: Schema['EncounterEnvelope']): EncounterEnvelope {
  return {
    version: dto.version ?? 0,
    changed: (dto.changed ?? {}) as Partial<EncounterSections>,
    notices: (dto.notices ?? []).map(toNotice),
  }
}

export interface EncounterMutationOptions<TVariables> {
  encounterId: number
  request: (variables: TVariables) => Promise<Schema['EncounterEnvelope']>
  onEnvelope?: (envelope: EncounterEnvelope) => void
  onFailure?: (error: unknown) => void
}

/**
 * The encounter counterpart of `useSectionMutation`.
 *
 * Both entries are patched, filtered. Worth knowing about the cards entry specifically:
 * a side change moves `core` and `sides`, and the cards entry holds `core` — so its
 * participant count follows along while its (expensive) `cards` list stays exactly as
 * fetched. That is correct: the API does not recompute cards on a side change either,
 * and the cards read is the only thing that produces them.
 */
export function useEncounterMutation<TVariables>(options: EncounterMutationOptions<TVariables>) {
  const queryClient = useQueryClient()

  return useMutation<EncounterEnvelope, unknown, TVariables>({
    mutationFn: async (variables) => toEnvelope(await options.request(variables)),

    onError: (error) => {
      options.onFailure?.(error)
    },

    onSuccess: (envelope) => {
      patchSheets<EncounterSections, EncounterSheet>(
        queryClient,
        [encounterKeys.sheet(options.encounterId), encounterKeys.cards(options.encounterId)],
        envelope,
      )

      // The one place a refetch is right rather than lazy: adding or removing a
      // participant changes *which* cards exist, and no envelope can carry that — the
      // cards section is served by its own endpoint and never appears in `changed`.
      // Invalidating leaves an open cards view to refetch itself and a closed one alone.
      if ('sides' in envelope.changed) {
        void queryClient.invalidateQueries({
          queryKey: encounterKeys.cards(options.encounterId),
        })
      }

      options.onEnvelope?.(envelope)
    },
  })
}

type Envelopes = Schema['EncounterEnvelope']

export const encounterApi = {
  // The body is the generated shape plus `type`, which postdates the last schema generation —
  // `useEncounterEditing` holds the widened type and the reasoning.
  update: (
    encounterId: number,
    body: Schema['UpdateEncounterRequest'] & { type?: Schema['EncounterType'] },
  ) => api.put<Envelopes>(`/encounters/${encounterId}`, body),

  addSide: (encounterId: number, sideName: string) =>
    api.post<Envelopes>(`/encounters/${encounterId}/sides`, { sideName }),

  renameSide: (encounterId: number, sideId: number, sideName: string) =>
    api.put<Envelopes>(`/encounters/${encounterId}/sides/${sideId}`, { sideName }),

  removeSide: (encounterId: number, sideId: number) =>
    api.delete<Envelopes>(`/encounters/${encounterId}/sides/${sideId}`),

  addCharacter: (encounterId: number, sideId: number, characterId: number) =>
    api.post<Envelopes>(`/encounters/${encounterId}/sides/${sideId}/characters`, { characterId }),

  // Addressed by the character id rather than the join row's — the route resolves the
  // character within the side.
  removeCharacter: (encounterId: number, sideId: number, characterId: number) =>
    api.delete<Envelopes>(`/encounters/${encounterId}/sides/${sideId}/characters/${characterId}`),
}
